import { useEffect, useRef, useState } from "react";
import ReactDOM, { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import defaultContent from "../../content/downlineProfile";
import { useAppDispatch, useAppSelector } from "../../store";
import { hideDownlineProfile, hideProfile, showDownlineProfile } from "../../reducers/profile";
import FocusTrap from "../../common/components/FocusTrap/FocusTrap";
import { IconClose } from "../../common/icons/IconClose/IconClose";
import DownlineProfileDataLayer from "./DownlineProfileDataLayer";
import DownlineProfile from "./DownlineProfile";
import "./DownlineProfileModal.scss";
import { useLockBodyScrolling } from "../../common/util/cssUtils";
import { IconChevronRight } from "../../common/icons/IconChevronRight/IconChevronRight";
import useProfileHistory from "./hooks/useProfileHistory";
import { useFullscreenPortalTarget } from "../../common/util/fullscreenPortalTarget";
import { trackBackProfile, trackCloseProfile, trackDownlineProfileView } from "./downlineProfileTealium";
import { setSelectedDownlineProfile } from "../../reducers/downlineProfile";

const DownlineProfileModal = () => {
  const { t } = useTranslation("downlineProfile");
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.profile.isShowDownlineProfile);
  const affAbo = useAppSelector((s) => s.profile.downlineProfileAffAbo);
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const isEnabled = profile?.enableDownlineProfile ?? false;
  const modalRef = useRef<HTMLDivElement>(null);

  const isShowProfile = useAppSelector((s) => s.profile.isShowProfile);
  const profileType = useAppSelector((s) => s.profile.profileType);
  const profileAffAbo = useAppSelector((s) => s.profile.affAbo);

  const profileParam = new URLSearchParams(window.location.search).get("profile");
  const isLegacyOverride = profileParam === "legacy";
  const useDownlineProfile = (isEnabled || profileParam === "downline") && !isLegacyOverride;
  const { showBackButton, goBack, pushToHistory } = useProfileHistory(affAbo);
  const currentAffAbo = useAppSelector((s) => s.downlineProfile.affAbo);

  useEffect(() => {
    if (isOpen && useDownlineProfile) {
      trackDownlineProfileView();
    }
  }, [isOpen, useDownlineProfile]);

  useEffect(() => {
    if (isShowProfile && profileType === "abo" && useDownlineProfile) dispatch(showDownlineProfile(profileAffAbo));
  }, [isShowProfile, profileType, profileAffAbo, useDownlineProfile, dispatch]);

  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const target = document.querySelector<HTMLDivElement>(".downline-profile__banner__nav-buttons");
    setPortalTarget(target);
  }, [isOpen]);

  const preventTouchMoves = (e: Event) => {
    const target = e.target as Node;
    if (modalRef?.current && modalRef.current.contains(target)) {
      // if touch is inside modal return to allow movement
      return;
    }
    if ((target as Element)?.closest?.(".metrics-toolbar__kpi-selector-portal, .kpi-tracking-filter-modal")) {
      // if touch is inside the KPI selector's portal (rendered outside modalRef's DOM subtree) return to allow movement
      return;
    }
    e.preventDefault();
  };
  useLockBodyScrolling(isOpen, modalRef, preventTouchMoves);

  // Keeps the modal visible while a TreeView-style fullscreen element is active —
  // otherwise this renders as a sibling outside the fullscreened subtree, which
  // the browser simply doesn't paint.
  const fullscreenPortalTarget = useFullscreenPortalTarget();

  if (!isOpen || !useDownlineProfile) return null;

  const handleClose = () => {
    dispatch(hideDownlineProfile());
    dispatch(hideProfile());
    dispatch(setSelectedDownlineProfile(""));
    trackCloseProfile();
  };

  return ReactDOM.createPortal(
    <FocusTrap handleOnClose={handleClose}>
      <div
        ref={modalRef}
        className="downline-profile-modal"
        data-testid="downline-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("title", defaultContent.title) as string}
      >
        {showBackButton &&
          portalTarget &&
          createPortal(
            <button
              type="button"
              className="downline-profile-modal__back"
              data-testid="downline-profile-modal-back"
              onClick={() => {
                goBack();
                trackBackProfile();
              }}
              aria-label={t("back", defaultContent.back)}
            >
              <IconChevronRight color="black" />
              <p>{t("back", defaultContent.back)}</p>
            </button>,
            portalTarget,
          )}
        {portalTarget &&
          createPortal(
            <button
              type="button"
              className="downline-profile-modal__close"
              data-testid="downline-profile-modal-close"
              onClick={handleClose}
              aria-label={t("close", defaultContent.close as string)}
            >
              <IconClose width="1.5rem" height="1.5rem" />
            </button>,
            portalTarget,
          )}
        <div className="downline-profile-modal__body">
          <DownlineProfileDataLayer affAbo={currentAffAbo}>
            <DownlineProfile affAbo={currentAffAbo} pushToHistory={pushToHistory} />
          </DownlineProfileDataLayer>
        </div>
      </div>
      <div className="downline-profile-modal__hidden" aria-hidden={true}></div>
    </FocusTrap>,
    fullscreenPortalTarget,
  );
};

export default DownlineProfileModal;
